"use client";
import { useEffect, useState } from "react";
import { Button, Modal, Table, Alert } from "react-bootstrap";
import ModalIconTitle from "../ModalIconTitle";
import { MdCancel } from "react-icons/md";
import CalendarBackend from "../Calendar/utils/CalendarBackend";
import common from "../../utils/common";
import moment from "moment";
import { AxiosResponse } from "axios";
import {
  CalendarApi,
  DateSelectArg,
  EventClickArg,
  EventContentArg,
} from "@fullcalendar/core/index.js";
import { useModalWidthClass } from "../../hooks/useModalWidth";
import { CalendarViewObject } from "../../class/calendar.schema";

type DeleteEventModalArgs = {
  show: boolean;
  onConfirmBtnClick: (
    resp: AxiosResponse,
    api: CalendarApi,
    id: number,
  ) => void;
  onClose: () => void;
  data: DateSelectArg | EventClickArg | EventContentArg | undefined;
  calendars?: CalendarViewObject[];
};

export default function DeleteEventModal({
  show,
  onConfirmBtnClick,
  onClose,
  data,
  calendars,
}: DeleteEventModalArgs) {
  const [id, setId] = useState(-1);
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [description, setDescription] = useState("");
  const [clickArgs, setClickArgs] = useState<EventClickArg>();
  const [calendarIds, setCalendarIds] = useState<number[]>();
  const dateFormat = "YYYY-MM-DD hh:mm A";
  const dialogClassName = useModalWidthClass();

  useEffect(() => {
    const args = data as EventClickArg;
    if (!args || !args.event) {
      return;
    }
    setId(common.integerTryParse(args.event?.id) ?? -1);
    setTitle(args.event?.title ?? "");
    if (args.event?.start !== null) {
      setStartDate(args.event?.start);
    }
    if (args.event?.end !== null) {
      setEndDate(args.event?.end);
    }
    setDescription(args.event?.extendedProps?.description ?? "");

    if (typeof args.event?.extendedProps?.calendarIds === "number") {
      setCalendarIds([args.event?.extendedProps?.calendarIds]);
    }
    if (Array.isArray(args.event?.extendedProps?.calendarIds)) {
      setCalendarIds(args.event?.extendedProps?.calendarIds);
    }

    setClickArgs(args);
  }, [data]);

  const onHide = () => {
    setId(-1);
    if (typeof onClose === "function") {
      onClose();
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      dialogClassName={dialogClassName}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <ModalIconTitle title={"Delete Event"} iconElement={<MdCancel />} />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant={"warning"}>
          <b>Warning:</b>
          <br />
          Are you sure to delete this Event?
          <br />
        </Alert>
        <Table bordered>
          <tbody>
            <tr>
              <th>Title</th>
              <td>{title}</td>
            </tr>
            <tr>
              <th>Description</th>
              <td>
                {description.split("\n").map((item, index) => (
                  <span key={index}>
                    {item}
                    <br />
                  </span>
                ))}
              </td>
            </tr>
            <tr>
              <th>Start Date</th>
              <td>{moment(startDate).format(dateFormat)}</td>
            </tr>
            <tr>
              <th>End Date</th>
              <td>{moment(endDate).format(dateFormat)}</td>
            </tr>
            <tr>
              <th>Calendar</th>
              <td>
                {calendarIds?.map((x) => {
                  const list = calendars as unknown as CalendarViewObject[];
                  return list.find((y) => y.id === x)
                    ? list.find((y) => y.id === x)?.name
                    : "";
                })}
              </td>
            </tr>
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <ConfirmButton
          id={id}
          clickArgs={clickArgs}
          customClickFunc={onConfirmBtnClick}
          onHide={onHide}
        />
        <CancelButton onHide={onHide} />
      </Modal.Footer>
    </Modal>
  );
}

type ConfirmButtonArgs = {
  id: number;
  clickArgs: EventClickArg | undefined;
  customClickFunc: (resp: AxiosResponse, api: CalendarApi, id: number) => void;
  onHide: () => void;
};

function ConfirmButton({
  id,
  clickArgs,
  customClickFunc,
  onHide,
}: ConfirmButtonArgs) {
  const confirmClick = async () => {
    if (!clickArgs) {
      return;
    }
    const calendarApi = clickArgs.view.calendar;
    calendarApi.unselect();
    const succFunc = (resp: AxiosResponse) => {
      if (typeof customClickFunc === "function") {
        if (!calendarApi) {
          return;
        }
        customClickFunc(resp, calendarApi, id);
      }
      onHide();
    };
    const errFunc = (resp: AxiosResponse | string) => {
      if (typeof customClickFunc === "function") {
        if (!calendarApi) {
          return;
        }
        customClickFunc(resp as AxiosResponse, calendarApi, id);
      }
      onHide();
    };
    return CalendarBackend.deleteRecord(id, succFunc, errFunc);
  };
  return (
    <Button variant="primary" onClick={confirmClick}>
      Confirm
    </Button>
  );
}

type CancelButtonArgs = {
  onHide: () => void;
};

function CancelButton({ onHide }: CancelButtonArgs) {
  return (
    <Button variant="secondary" onClick={onHide}>
      Cancel
    </Button>
  );
}
